import { DateTime } from 'luxon';

export function dateToTimestamp(date: string | number | Date): number {
  try {
    return new Date(date)?.getTime() ?? 0;
  } catch (error) {
    console.error(error);
    return 0;
  }
}

export function adjustDateToNow(date: Date | string | number): Date {
  let parsed = new Date(date);
  if (!parsed || isNaN(parsed.getTime())) {
    parsed = new Date();
  }
  // use luxon to get the offset in minutes, convert to milliseconds
  const offset = DateTime.fromJSDate(parsed).offset * 60 * 1000;
  return new Date(parsed.getTime() + offset);
}

export function utcTimestamp(ts: number): number {
  const offset = new Date().getTimezoneOffset();
  return ts - (offset * 60 * 1000);
}

export function utcDate(fromDate = new Date()): Date {
  const offset = new Date(fromDate).getTimezoneOffset();
  const date = new Date();
  date.setMinutes(date.getMinutes() + offset);
  return date;
}

export function toIsoDate(date: Date | number | string, withTime?: boolean): string | null {
  const parsed = new Date(date);
  if (!parsed) {
    return null;
  }
  const offset = parsed.getTimezoneOffset() * 60000;
  const isoString = new Date(parsed.getTime() - offset)?.toISOString();
  if (!withTime) {
    return isoString?.split('T')?.[0];
  }
  return isoString;
}

export function toHrDate(date: Date | number | string): string {
  if (!date) {
    return '';
  }
  const isoDate = toIsoDate(date);
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
}

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

// DOB is a calendar date: take the yyyy-mm-dd part, never a tz round-trip.
export function toDateOnly(raw: string | number | Date | null | undefined): string {
  if (!raw) {
    return '';
  }
  if (typeof raw === 'string') {
    const match = ISO_DATE_PREFIX.exec(raw.trim());
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
  }
  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
}

export function dateOnlyToUs(raw: string | number | Date | null | undefined): string {
  const iso = toDateOnly(raw);
  if (!iso) {
    return '';
  }
  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
}

export function toHrTime(date: Date | number | string): string {
  if (!date) {
    return '';
  }
  const dateObject = new Date(date);
  const iso = DateTime.fromJSDate(dateObject);
  return iso.toFormat('hh:mm a');
}

export function formatToMMYY(date: Date | string) {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}
